import {RebuildingScene} from "../RebuildingScene";
import {DoorTopDown} from "../../shared-2d/DoorTopDown";
import {FrmSceneLayout} from "../../drawfrm/FrmSceneLayout";
import {ElevationApp} from "../../ElevationApp";
import {getProfileObj, getSwingSide, linkProfileAndDoor, openingToFromLabel, profilesShareYOrientation, TProfile} from "./Helpers";
import {AddFx, ChildView} from "../../shared-2d/ChildView";
import {Label} from "../../shared-2d/Label";
import {NavButtons} from "../../shared-2d/NavButtons";
import {doorSideNeighbour} from "../frameGrid/BlockOps";
import {stickThicknessVar} from "../frameGrid/Util";
import {itemProfile} from "../../model/frame/MdFrameBlock";

export type FrameJambSide = "left" | "right";

class FrameProfileView extends ChildView {
    profile: TProfile;
    // the opposite (lite) face of a double-sided mullion: present only when a
    // lite/panel sits across the shown jamb (see doorSideNeighbour). null otherwise.
    liteProfile: TProfile | null = null;
    door: DoorTopDown;

    topLabel: Label;
    bottomLabel: Label;

    constructor(elev: ElevationApp, add: AddFx, active: boolean) {
        super(elev, add);

        // the jamb side + swing follow directly from which leaf we're viewing. Fall
        // back to a sensible default for no-swing handings (getSwingSide throws), so
        // the profile still renders.
        let swingInfo: {side: FrameJambSide; direction: "in" | "out" | "both"};
        try {
            swingInfo = getSwingSide(this.elev, active);
        } catch {
            swingInfo = {side: active ? "left" : "right", direction: "in"};
        }

        // a lite/panel across this jamb turns it into a double-sided (clamshell)
        // mullion; the door side then hides its returns so the two profiles merge.
        const neighbour = doorSideNeighbour(this.elev.frameSpec, swingInfo.side);

        this.profile = getProfileObj({
            elev: this.elev,
            side: swingInfo.side,
            view: this,
            direction: swingInfo.direction,
            detail: this.elev.frameSpec.doorFrameProfile,
            jambDepth: this.elev.frameSpec.jambDepth,
            jambFace: this.elev.frameSpec.jambFace,
            clamshell: !!neighbour,
        });

        this.topLabel = openingToFromLabel(this.elev, "one");
        this.add(this.topLabel);

        // add door first so profile (and dimensions) is on top. Draw the leaf we're
        // viewing (active/inactive) so a pair's inactive view shows its own leaf.
        this.door = new DoorTopDown(this.elev, active);
        this.add(this.door);
        this.add(this.profile);

        // dimensioning detail is driven by the global "Simple Dimensions" ribbon
        // toggle (menubar/ViewGroups): standard = simple, extended = detailed.
        this.trackSubscription(this.elev.viewSettings.dimSimple.subscribeAndFireLast(simple => {
            this.dimensionMode.emit(simple ? "standard" : "extended");
        }));

        linkProfileAndDoor({
            elev: this.elev,
            door: this.door,
            profile: this.profile,
            side: swingInfo.side,
            direction: swingInfo.direction,
        });

        this.buildDoubleSidedMullion(swingInfo, neighbour);

        this.bottomLabel = openingToFromLabel(this.elev, "two");
        this.add(this.bottomLabel);
    }

    // when a lite/panel sits across the shown jamb's mullion, draw the mullion as a
    // clamshell: the door-side profile stays as-is (this.profile) and a second profile
    // for the neighbour's own profile is drawn mirrored, offset from the door edge by
    // the mullion (stick) thickness. No neighbour → single jamb, unchanged.
    private buildDoubleSidedMullion(
        swingInfo: {side: FrameJambSide; direction: "in" | "out" | "both"},
        neighbour: ReturnType<typeof doorSideNeighbour>,
    ) {
        if(!neighbour) return;
        const spec = this.elev.frameSpec;

        // mirror the lite side (opposite jamb side flips invertX) so its door edge
        // faces the lite; it uses the neighbour cell's own profile, clamshelled so its
        // returns are hidden too. The cut-dimension detail is the frame's — per-cell
        // detail isn't modeled yet, only the profile type.
        const oppositeSide: FrameJambSide = swingInfo.side === "left" ? "right" : "left";
        const liteProfileType = itemProfile(spec, neighbour.item);
        this.liteProfile = getProfileObj({
            elev: this.elev,
            side: oppositeSide,
            view: this,
            direction: swingInfo.direction,
            detail: spec.doorFrameProfile,
            jambDepth: spec.jambDepth,
            jambFace: spec.jambFace,
            profile: liteProfileType,
            clamshell: true,
            // the lite side is the secondary half: it drops its top/bottom faces so
            // the door side's faces cap the merged mullion (no doubled/gapped cap).
            hideFaces: true,
            // DE/DW/SL/SR draw with the opposite invertY convention, so flip the lite
            // side to share the door side's vertical orientation when they differ —
            // otherwise the two halves' faces align top-to-bottom.
            flipY: !profilesShareYOrientation(spec.profile, liteProfileType),
        });
        this.add(this.liteProfile);

        // The two halves interleave: each side's rabbet sits at the other side's outer
        // face. Aligning the faceAnchors (top of the outer face) keeps them level even
        // when the profiles differ; offsetting by (thickness - 2*jambFace) puts the two
        // rabbets exactly `thickness` apart, so the kept door-side top/bottom faces span
        // the mullion and cap it. For a default mullion thickness == jambFace, so the
        // offset is -jambFace (the lite half tucks back under the door-side faces).
        const thickness = stickThicknessVar(neighbour.stick, spec).get();
        const offset = thickness - 2 * spec.jambFace.get();
        const dx = swingInfo.side === "left" ? -offset : offset;
        this.liteProfile.faceAnchor.linkTo(this.profile.faceAnchor, dx, 0);
    }

    getLayout(navButtons?: any) {
        if(navButtons) this.add(navButtons);

        return FrmSceneLayout.column(
            navButtons ? FrmSceneLayout.absolute(navButtons, {
                top: 0,
                right: 0,
            }) : undefined,
            FrmSceneLayout.row(this.topLabel).justifyContent("center"),
            FrmSceneLayout.group([
                this.door, this.profile, this.liteProfile,
            ]).grow(1),
            FrmSceneLayout.row(this.bottomLabel).justifyContent("center"),
        )
    }
}

export class FrameProfileScene extends RebuildingScene {

    profile: TProfile | null = null;
    door: any | null = null;
    active: boolean;

    constructor(app: ElevationApp, active: boolean = true) {
        super(app);
        this.active = active;
    }

    protected rebuildOnValueEdit(): boolean {
        return true;
    }

    protected build(): void {
        const view = new FrameProfileView(this.elev, item => this.add(item), this.active);

        this.setupLayout(
            view.getLayout(new NavButtons({
                collapse: () => this.elev.navTo("frameTopDown"),
            }))
                .grow(1)
                .gap(20)
                .padding(20),
        );
    }
}